import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Clock, Download, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseImport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [job, setJob] = useState(null);
  const [mapping, setMapping] = useState({});
  const [review, setReview] = useState(null);
  const [tab, setTab] = useState("valid");
  const [mode, setMode] = useState("skip_duplicates");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);

  if (!hasPerm(user, "expenses.edit")) {
    return (
      <div className="mx-auto max-w-2xl mt-10 rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="exp-import-no-access">
        <AlertTriangle className="mx-auto h-6 w-6 text-slate-300" />
        <div className="mt-2 font-medium text-slate-700">You don’t have access to bulk expense import</div>
        <div className="mt-1 text-sm text-slate-500">You need the Expenses edit permission.</div>
      </div>
    );
  }

  const dl = (fmt) => window.open(`${api.defaults.baseURL}/import/template?import_type=expenses&fmt=${fmt}`, "_blank");

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("import_type", "expenses");
    try {
      const r = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(r.data); setMapping(r.data.suggested_mapping || {});
      setStep(r.data.sheets?.length > 1 ? 2 : 3);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/import/${job.id}/validate`, { mapping, sheet: job.sheet });
      setReview(r.data); setStep(4);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      await api.post(`/import/${job.id}/commit`, { mode });
      setStep(5); pollStatus();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const pollStatus = () => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/import/${job.id}`);
        setStatus(r.data);
        if (["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"].includes(r.data.status)) clearInterval(t);
      } catch { clearInterval(t); }
    }, 1200);
  };

  const STEPS = ["Upload", "Sheet", "Confirm", "Review", "Done"];
  return (
    <div data-testid="expense-import-page">
      <PageHeader title="Bulk Expense Import" subtitle="Import recorded business costs from a spreadsheet — same rules as manual entry">
        <Button variant="outline" onClick={() => navigate("/finance")} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to Finance</Button>
      </PageHeader>

      <div className="mb-5 flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <div key={s} className={`rounded-full px-3 py-1 ${step >= i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{i + 1}. {s}</div>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="exp-import-step-upload" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="exp-template-panel">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Download className="h-4 w-4 text-emerald-600" /> Download the expense template</div>
            <div className="mt-1 text-xs text-slate-500">Columns: Date, Category, Subcategory, Description, Amount, Quantity, Unit, Unit Cost, Vendor, Payment Method, Reference No, Service, Expense Type, Recurring, CAPEX, Notes. Required: Date, Category, Amount. Delete the example rows before uploading.</div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" data-testid="exp-template-csv" onClick={() => dl("csv")}><Download className="h-3 w-3" /> CSV</Button>
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" data-testid="exp-template-xlsx" onClick={() => dl("xlsx")}><Download className="h-3 w-3" /> XLSX</Button>
            </div>
          </div>
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
            <Upload className="mx-auto h-8 w-8 text-slate-300" />
            <div className="mt-2 text-sm text-slate-600">Upload a CSV or XLSX file</div>
            <input ref={fileRef} type="file" data-testid="exp-import-file-input" className="hidden" accept=".csv,.tsv,.json,.xlsx,.xls" onChange={(e) => upload(e.target.files[0])} />
            <Button data-testid="exp-import-choose-file" onClick={() => fileRef.current?.click()} disabled={busy} className="mt-4 bg-slate-900 text-white">{busy ? "Reading…" : "Choose file"}</Button>
            <div className="mt-2 text-xs text-slate-400">Max 25 MB / 100,000 rows.</div>
          </div>
        </div>
      )}

      {step === 2 && job && (
        <div data-testid="exp-import-step-sheet" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="font-medium text-slate-800">Select worksheet</div>
          <select data-testid="exp-import-sheet-select" value={job.sheet || job.sheets[0]} onChange={(e) => setJob({ ...job, sheet: e.target.value })}
            className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            {job.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="mt-4"><Button data-testid="exp-import-next-sheet" onClick={() => setStep(3)} className="bg-slate-900 text-white">Continue</Button></div>
        </div>
      )}

      {step === 3 && job && (
        <div data-testid="exp-import-step-confirm" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="text-sm text-slate-600"><FileSpreadsheet className="inline h-4 w-4" /> {job.filename} · {job.file_type?.toUpperCase()} · {job.row_count} rows</div>
          <div className="mt-3 font-medium text-slate-800">Detected columns</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(job.columns || []).map((col) => (
              <span key={col} className={`rounded-full px-2.5 py-1 text-xs ${mapping[col] ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-400"}`}>
                {col}{mapping[col] ? ` → ${mapping[col]}` : " (ignored)"}
              </span>
            ))}
          </div>
          <div className="mt-5 flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button data-testid="exp-import-validate-btn" onClick={validate} disabled={busy} className="bg-slate-900 text-white">{busy ? "Checking…" : "Review expenses"} <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {step === 4 && review && (
        <div data-testid="exp-import-step-review" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="grid gap-3 sm:grid-cols-5">
            {[["total", "Total", "text-slate-800"], ["valid", "Ready", "text-emerald-600"], ["warning", "Warnings", "text-amber-600"], ["duplicate", "Duplicates", "text-sky-600"], ["error", "Errors", "text-rose-600"]].map(([k, l, c]) => (
              <div key={k} data-testid={`exp-count-${k}`} className="rounded-lg border border-slate-200 p-3 text-center">
                <div className={`font-display text-2xl font-bold ${c}`}>{review.counts[k]}</div><div className="text-xs text-slate-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-500">Nothing has been imported yet. Review possible duplicates before importing.</div>
          <div className="mt-4 flex gap-1 border-b border-slate-200 text-sm">
            {["valid", "warning", "duplicate", "error"].map((t) => (
              <button key={t} data-testid={`exp-tab-${t}`} onClick={() => setTab(t)} className={`px-3 py-2 ${tab === t ? "border-b-2 border-emerald-500 text-slate-800" : "text-slate-400"}`}>{t} ({review.counts[t]})</button>
            ))}
          </div>
          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Category</th><th className="px-2 py-1.5">Description</th><th className="px-2 py-1.5 text-right">Amount</th><th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Vendor</th><th className="px-2 py-1.5">Issues</th></tr></thead>
              <tbody>{(review.samples[tab] || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{r.row}</td>
                  <td className="px-2 py-1.5">{r.expense?.expense_date || "—"}</td>
                  <td className="px-2 py-1.5 capitalize">{r.expense?.category || "—"}</td>
                  <td className="px-2 py-1.5">{r.expense?.description || r.expense?.subcategory || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{r.expense?.amount ?? "—"}</td>
                  <td className="px-2 py-1.5">{r.expense?.is_capex ? "CAPEX" : (r.expense?.expense_type || "—")}</td>
                  <td className="px-2 py-1.5">{r.expense?.vendor || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-500">{[...(r.errors || []), ...(r.warnings || [])].map((e) => e[1]).join("; ") || (r.duplicate ? "Possible duplicate — review" : "")}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select data-testid="exp-mode-select" value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="skip_duplicates">Skip duplicates</option>
              <option value="import_new">Import all (including possible duplicates)</option>
            </select>
            <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/import/${job.id}/errors`, "_blank")} className="gap-1.5" data-testid="exp-download-errors"><Download className="h-4 w-4" /> Error report</Button>
            <Button data-testid="exp-commit-btn" onClick={commit} disabled={busy || review.counts.valid + review.counts.warning === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white">Import expenses</Button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div data-testid="exp-import-step-done" className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          {status && ["COMPLETED", "COMPLETED_WITH_ERRORS"].includes(status.status) ? (<>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <div className="mt-2 font-display text-xl font-bold text-slate-800">Import {status.status === "COMPLETED_WITH_ERRORS" ? "completed with some errors" : "complete"}</div>
            <div className="mt-2 text-sm text-slate-600" data-testid="exp-import-result">
              {status.result?.expenses_created || 0} expenses created · {status.result?.skipped || 0} skipped · {status.result?.failed || 0} failed
            </div>
            <Button onClick={() => navigate("/finance")} className="mt-5 bg-slate-900 text-white" data-testid="exp-import-view-finance">View Finance</Button>
          </>) : (<>
            <Clock className="mx-auto h-10 w-10 animate-pulse text-slate-400" />
            <div className="mt-2 font-medium text-slate-700">Importing…</div>
            <div className="mt-1 text-sm text-slate-500" data-testid="exp-import-progress">{status?.progress || 0} / {job?.row_count || 0}</div>
          </>)}
        </div>
      )}
    </div>
  );
}
